import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api, ApiError } from '@/lib/api.js';
import { cn } from '@/lib/utils.js';
import CrmContactPicker from '@/components/licenses/CrmContactPicker.jsx';
import { searchProducers } from '@/lib/producerSearch.js';

// ── PolicyInfoDialog — THE broker door for policy-identity fields ─────
// (TRACON-FIELD-GAP-INVENTORY.md, 2026-07-23)
//
// Policy number, effective date, expiration date, carrier, producer. One component,
// mounted from two places (same one-door rule RosterSheet follows):
//   - deal workspace action bar ("Edit policy info")
//   - LPRSheet's policy card + preview error state
//
// PATCHes /api/tracon/deal/:id/policy-info, which funnels into
// services/policy-info.service.js (the single Deal writer). Only DIRTY
// fields ride the request: an untouched input never writes.
//
// Carrier picker reads /api/tracon/policy-info/carriers (Supabase carriers,
// the domain list). Rows without a CRM vendor mapping render disabled so
// the broker learns the fix (carrier admin) instead of hitting a 400.

const selectClass = cn(
  'flex min-h-[44px] w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg'
);

function ymd(v) {
  if (!v) return '';
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

// A term the broker TYPED (1..60 whole months), or null. The browser derives
// no term from the dates: the server works it out, marks it derived, and
// returns it on the preview (review M2, One Fact One Derivation).
function typedTermMonths(v) {
  const t = String(v || '').trim();
  if (!/^\d{1,2}$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 60 ? n : null;
}

export default function PolicyInfoDialog({ dealId, open, onOpenChange, initial, onSaved }) {
  // Baseline the form from the caller-provided snapshot each time the
  // dialog opens (the workspace item or LPR deal context).
  const base = useMemo(() => ({
    policy_number: initial?.policy_number || '',
    policy_start: ymd(initial?.policy_start),
    policy_end: ymd(initial?.policy_end || initial?.policy_expiration),
    carrier_name: initial?.carrier_name || initial?.carrier || '',
  }), [initial]);

  const [form, setForm] = useState(base);
  const [carrierId, setCarrierId] = useState(''); // '' = keep current carrier
  // The producer of record (David 2026-11-01: a policy detail, written only through this door). { id, name } | null;
  // sent only when touched, so an untouched picker never writes.
  const baseProducer = useMemo(() => (initial?.producer_id ? { id: String(initial.producer_id), name: initial?.producer_name || '' } : null), [initial]);
  const [producer, setProducer] = useState(baseProducer);
  const [producerTouched, setProducerTouched] = useState(false);
  const [carriers, setCarriers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // THE CORRECTION HALF (decision crossing 8, client side, 2026-09-04). On a
  // bound deal the writer refuses a date change with PERIOD_LEDGERED, because
  // the period is the ledger's fact. The same dialog then previews the period
  // correction (what moves where, the term) and commits it with a reason. No
  // new door: this is the sender for a route that had none.
  const [correction, setCorrection] = useState(null); // preview payload | null
  const [reason, setReason] = useState('');
  // The server answered PERIOD_LEDGERED: the period moves through a correction,
  // and the broker may state the policy's term (blank: derived from the dates).
  const [ledgered, setLedgered] = useState(false);
  const [statedTerm, setStatedTerm] = useState('');

  useEffect(() => {
    if (!open) { setCorrection(null); setReason(''); setLedgered(false); setStatedTerm(''); return; }
    setForm(base);
    setCarrierId('');
    setProducer(baseProducer);
    setProducerTouched(false);
    setError(null);
  }, [open, base, baseProducer]);

  // Lazy carrier list: fetched once per dialog open.
  useEffect(() => {
    if (!open || carriers !== null) return;
    api
      .get('/api/tracon/policy-info/carriers')
      .then((res) => setCarriers(res?.carriers || []))
      .catch(() => setCarriers([]));
  }, [open, carriers]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // Dirty-field patch: absent key = field untouched on the server.
  const patch = useMemo(() => {
    const p = {};
    const num = form.policy_number.trim();
    if (num && num !== (base.policy_number || '').trim()) p.policy_number = num;
    if (form.policy_start && form.policy_start !== base.policy_start) p.policy_start = form.policy_start;
    if (form.policy_end && form.policy_end !== base.policy_end) p.policy_end = form.policy_end;
    if (carrierId) p.carrier_id = carrierId;
    if (producerTouched && (producer?.id || null) !== (baseProducer?.id || null)) p.producer_contact_id = producer?.id || null;
    return p;
  }, [form, base, carrierId, producer, producerTouched, baseProducer]);

  const dirty = Object.keys(patch).length > 0;

  function validate() {
    const start = form.policy_start || base.policy_start;
    const end = form.policy_end || base.policy_end;
    if (start && end && end <= start) {
      return 'Expiration must be after the effective date.';
    }
    return null;
  }

  async function handleSave() {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    if (ledgered) { await loadCorrectionPreview(); return; }
    setSaving(true);
    try {
      const res = await api.patch(
        `/api/tracon/deal/${encodeURIComponent(dealId)}/policy-info`,
        patch
      );
      if (!res?.success) throw new Error(res?.error || 'Save failed');
      toast.success(
        res.updated?.length
          ? `Policy info saved (${res.updated.join(', ')})`
          : 'No changes to save'
      );
      onOpenChange?.(false);
      if (typeof onSaved === 'function') onSaved(res);
    } catch (err) {
      if (err instanceof ApiError && err.body?.code === 'PERIOD_LEDGERED') {
        setLedgered(true);
        await loadCorrectionPreview();
        return;
      }
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Preview writes nothing. Both dates always ride along: the door resolves
  // the end from the term when only the effective moved.
  async function loadCorrectionPreview() {
    const effective = form.policy_start || base.policy_start;
    // The end rides along only when the broker changed it; otherwise the door
    // derives it from the stated term, so moving the effective alone moves
    // the whole period instead of tripping the whole-term refusal.
    const end = form.policy_end && form.policy_end !== base.policy_end ? form.policy_end : '';
    // A term rides along ONLY when the broker typed one (stated). Otherwise the
    // server derives it from the dates and the preview says which it is.
    const months = typedTermMonths(statedTerm);
    try {
      const qs = `effective_date=${encodeURIComponent(effective)}${end ? `&expiration_date=${encodeURIComponent(end)}` : ''}${months ? `&term_months=${months}` : ''}`;
      const res = await api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/correction/period?${qs}`);
      if (!res?.success) throw new Error(res?.error || 'Preview failed');
      setCorrection(res);
      setError(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message;
      setError(msg || 'Preview failed');
    }
  }

  async function handleCommitCorrection() {
    if (!reason.trim()) { setError('A correction records why. Add a reason.'); return; }
    setError(null);
    setSaving(true);
    try {
      const res = await api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/correction/period`, {
        effective_date: correction.expected.effective,
        expiration_date: correction.expected.end,
        ...(typedTermMonths(statedTerm) ? { term_months: typedTermMonths(statedTerm) } : {}),
        reason: reason.trim(),
      });
      if (!res?.success) throw new Error(res?.error || 'Correction failed');
      const doc = res.document?.link ? ' Document regenerated.' : '';
      const partial = Array.isArray(res.partial) && res.partial.length ? ` Note: ${res.partial.join('; ')}` : '';
      toast.success(`Policy period corrected to ${res.expected.effective} to ${res.expected.end}.${doc}${partial}`);
      onOpenChange?.(false);
      if (typeof onSaved === 'function') onSaved(res);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      const disagree = body?.readBack?.disagreements?.length ? ` ${body.readBack.disagreements.join('; ')}` : '';
      setError((body?.error || err.message || 'Correction failed') + disagree);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit policy info</DialogTitle>
          <DialogDescription>
            Saved to the deal record. Documents (LPR, SNKL, binding) resolve
            these fields fresh, so fixes land on the next preview.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Policy number</span>
            <Input
              value={form.policy_number}
              onChange={(e) => setField('policy_number', e.target.value)}
              placeholder="e.g. SVRD01543490"
              className="mt-1.5 font-mono"
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Effective</span>
              <Input
                type="date"
                value={form.policy_start}
                onChange={(e) => setField('policy_start', e.target.value)}
                className="mt-1.5"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Expiration</span>
              <Input
                type="date"
                value={form.policy_end}
                onChange={(e) => setField('policy_end', e.target.value)}
                className="mt-1.5"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Carrier</span>
            <select
              value={carrierId}
              onChange={(e) => setCarrierId(e.target.value)}
              className={cn(selectClass, 'mt-1.5')}
            >
              <option value="">
                {base.carrier_name ? `Keep current (${base.carrier_name})` : 'Select a carrier'}
              </option>
              {(carriers || []).map((c) => (
                <option key={c.id} value={c.id} disabled={!c.linkable}>
                  {c.name}{c.linkable ? '' : ' (no CRM vendor link, fix in carrier admin)'}
                </option>
              ))}
            </select>
          </label>

          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Producer</span>
            <p className="mt-1 text-[11px] text-text-dim">Who produced this business. Sets the brand on the application, binder and endorsements: a producer under IBI Aviation prints IBI. Leave empty for NDEMNIFLY.</p>
            <div className="mt-1.5">
              <CrmContactPicker
                value={producer}
                searchFn={searchProducers}
                placeholder="Search producers (Contact type Producer)…"
                onSelect={(c) => { setProducer(c ? { id: String(c.id), name: c.name || '' } : null); setProducerTouched(true); }}
              />
            </div>
          </div>

          {ledgered ? (
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Term stated on the policy (months)</span>
              <Input
                inputMode="numeric"
                value={statedTerm}
                onChange={(e) => { setStatedTerm(e.target.value); setCorrection(null); }}
                placeholder="Leave blank to work it out from the dates"
                className="mt-1.5"
              />
            </label>
          ) : null}

          {correction ? (
            <div className="space-y-2 rounded-card border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-text">
              <p className="font-semibold">This policy is bound, so the period moves through a correction.</p>
              <p className="text-text-muted">
                Ledger {correction.before?.ledger ? `${correction.before.ledger.effective} to ${correction.before.ledger.end}` : 'none'}
                {' '}and signed record {correction.before?.binding_data ? `${correction.before.binding_data.effective} to ${correction.before.binding_data.end}` : 'none'}
                {' '}become <span className="font-semibold text-text">{correction.expected.effective} to {correction.expected.end}</span>
                {correction.term?.months ? ` (${correction.term.months}-month term, ${correction.term.source})` : ''}.
                {' '}The Deal and the signed application follow, and the change is recorded with your reason.
              </p>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Reason</span>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. carrier bound effective 9/8 per binder"
                  className="mt-1.5"
                />
              </label>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-card border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={saving}>
            Cancel
          </Button>
          {correction ? (
            <Button
              onClick={handleCommitCorrection}
              disabled={saving || !reason.trim()}
              className="bg-accent text-white hover:bg-accent/90"
            >
              {saving ? 'Correcting…' : 'Correct the period'}
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={saving || (!dirty && !ledgered)}
              className="bg-accent text-white hover:bg-accent/90"
            >
              {saving ? 'Saving…' : ledgered ? 'Preview the correction' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
